import { DateTime } from "luxon";
import { useMemo } from "react";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { isNurseActiveInDepartment } from "../../utils/formatters";
import { hasHrisIntegrationOfType } from "../../utils/hrisIntegrations";
import { type ActualNurse, NotificationType, type Nurse, type NurseInShift, type OpenShiftRequest, Preferences } from "../../utils/models";
import { isAgencyNurse } from "../../utils/utilFunctions";
import type { TeamStatsModel } from "./config";

export type TeamStatsResult = TeamStatsModel & {
	workedLastMonthNurseIds: Set<number>;
	/**
	 * Still computed because Management Center reports a weekly-active RATE off it (at a 30-day span).
	 * The Team page shows `neverLoggedInNurseIds` instead — see the comment there.
	 */
	weeklyActiveNurseIds: Set<number>;
	neverLoggedInNurseIds: Set<number>;
	recurringShiftsNurseIds: Set<number>;
	selfSchedulingSubmittedNurseIds: Set<number>;
};

export const useGetTeamStats = ({ filteredNurses, appActiveDaySpan = 7 }: { filteredNurses?: Nurse[]; appActiveDaySpan?: number }) => {
	const { staffRequests } = useRequestsContext();
	const { hospitalData, chosenDepartment } = useHospitalData();
	const isTherePayrollIntegration = hasHrisIntegrationOfType(chosenDepartment, "shifts");
	return useMemo<TeamStatsResult>(() => {
		const nursesToUse = filteredNurses ?? hospitalData.nurses;
		const activeNurses = nursesToUse.filter((n) => chosenDepartment && isNurseActiveInDepartment(n, chosenDepartment.department_id));
		const activeSpan = DateTime.now().minus({ days: appActiveDaySpan });

		const openShiftsCount = staffRequests
			.filter((req) => req.request_type === NotificationType.OpenShift)
			.reduce((total, req) => total + ((req as OpenShiftRequest).needed_nurses || 1), 0);

		// Computed over the full population (not just active staff) so the set is complete for the
		// stat-card filter and so the "Active Only" toggle can narrow the displayed count itself.
		const weeklyActiveNurseIds = new Set<number>();
		nursesToUse.forEach((n) => {
			if (n.lastLoginTime && n.lastLoginTime > activeSpan) weeklyActiveNurseIds.add(n.id);
		});

		/**
		 * Staff who have never opened the app.
		 *
		 * This replaced a "Weekly active" card that read 0 on facilities with 130 active staff, which
		 * looked like a broken metric and was really a true one: `lastLoginTime` comes from the joined
		 * `user` row, so every nurse without an app account reads as never-logged-in and can never read
		 * as anything else. Counting the quiet end states that plainly — and it is the question managers
		 * actually ask of this column ("who has gone quiet"), rather than its inverse.
		 */
		const neverLoggedInNurseIds = new Set<number>();
		nursesToUse.forEach((n) => {
			if (!n.lastLoginTime) neverLoggedInNurseIds.add(n.id);
		});

		const onBoardedCount = nursesToUse.filter((n) => !isAgencyNurse(n.employmentType) && !!n.lastLoginTime).length;

		const oneMonthAgo = DateTime.now().minus({ months: 1 });
		const now = DateTime.now();
		const oneMonthFromNow = DateTime.now().plus({ months: 1 });
		const candidateNurseIds = new Set(nursesToUse.map((n) => n.id));
		const workedLastMonthNurseIds = new Set<number>();
		const worksInNextMonthNurseIds = new Set<number>();
		const recurringShiftsNurseIds = new Set<number>();

		hospitalData.weeks.forEach((week) => {
			week.weeklyShifts.forEach((shift) => {
				shift.days.forEach((day) => {
					const isInPastMonth = day.date >= oneMonthAgo && day.date <= now;
					const isInNextMonth = day.date > now && day.date <= oneMonthFromNow;
					if (!isInPastMonth && !isInNextMonth) return;

					if (isInPastMonth) {
						(isTherePayrollIntegration ? day.nursesActual : day.nurses)?.forEach((nurseInShift: NurseInShift | ActualNurse) => {
							if (candidateNurseIds.has(nurseInShift.nurseId)) workedLastMonthNurseIds.add(nurseInShift.nurseId);
						});

						day.nurses?.forEach((nurseInShift: NurseInShift) => {
							if (nurseInShift.recurringShiftId != null && candidateNurseIds.has(nurseInShift.nurseId)) {
								recurringShiftsNurseIds.add(nurseInShift.nurseId);
							}
						});
					}

					if (isInNextMonth) {
						day.nurses?.forEach((nurseInShift: NurseInShift) => {
							if (candidateNurseIds.has(nurseInShift.nurseId)) worksInNextMonthNurseIds.add(nurseInShift.nurseId);
						});
					}
				});
			});
		});

		const activeNursesInPastOrNextMonth = activeNurses.filter((n) => workedLastMonthNurseIds.has(n.id) || worksInNextMonthNurseIds.has(n.id)).length;

		const selfSchedulingSubmittedNurseIds = new Set<number>();
		let selfSchedulingSubmissionsCount: number | undefined;
		if (chosenDepartment.selfScheduling) {
			const { periodStart, periodEnd } = chosenDepartment.selfScheduling;
			nursesToUse.forEach((nurse) => {
				const hasPreferencesInPeriod = nurse.employeeSpecificPreferences?.some(
					(pref) => pref.type === Preferences.Prefer && pref.start_date >= periodStart && pref.start_date <= periodEnd,
				);
				if (hasPreferencesInPeriod) selfSchedulingSubmittedNurseIds.add(nurse.id);
			});
			selfSchedulingSubmissionsCount = selfSchedulingSubmittedNurseIds.size;
		}

		return {
			totalStaff: nursesToUse.length,
			activeStaff: activeNurses.length,
			openShifts: openShiftsCount,
			// Active-only count, preserved for the Management Center statistics which always reports
			// active staff regardless of the Team page's "Active Only" toggle.
			activeUsersOnApp: activeNurses.filter((n) => weeklyActiveNurseIds.has(n.id)).length,
			onBoarded: onBoardedCount,
			selfSchedulingSubmissions: selfSchedulingSubmissionsCount,
			activeNursesInPastOrNextMonth,
			workedLastMonthNurseIds,
			weeklyActiveNurseIds,
			neverLoggedInNurseIds,
			recurringShiftsNurseIds,
			selfSchedulingSubmittedNurseIds,
		};
	}, [filteredNurses, staffRequests, hospitalData, appActiveDaySpan, chosenDepartment, isTherePayrollIntegration]);
};
